import React, { useMemo, useState } from 'react';
import Axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import I18n from '@/modules/i18n';
import * as Routes from '@/modules/routes';
import tpmToast from '@/components/admin/SlideShow/PresentMode/tpmToast';
import { UnstyledButton } from '@/components/ui/Buttons';
import Icon from '@/components/ui/Icon';
import Popover from '@/components/ui/Popover';
import Text from '@/components/ui/Text';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import cn from '@/utils/cn';
import StudentRow from './StudentRow';
import { RosterStudent, useStudentsRoster } from './useStudentsRoster';

// AIDEV-NOTE: PLAT-16888 — Renders the popover contents (header / Joined / Not Yet Joined /
// Manage Students footer). Anchored above the PresentingCount toolbar button via Radix Popover
// in PresentingCount.tsx — this component never positions itself.
// PLAT-16784 / PLAT-17342 / PLAT-17386 — Each Joined StudentRow's inline "Remove? Yes" popover
// DELETEs to /live_sessions/:id/students/:student_id and optimistically adds the id to
// `locallyRemovedIds` so the row drops immediately (rolled back on error). Server-side removals
// reach the panel via `invitedStudentIds` (same shape from the initial roster fetch and
// `student_count_changed` broadcasts), so removed students just disappear — no separate
// removed-ids set to merge.
const StudentsPanel = () => {
  const activeSessionId = useSlideShowContext(state => state.activeSessionId);
  const connectedStudentIds = useSlideShowContext(state => state.connectedStudentIds);
  const invitedStudentIds = useSlideShowContext(state => state.invitedStudentIds);
  const openNotifyModal = useSlideShowContext(state => state.openNotifyModal);
  const studentsPanelOpen = useSlideShowContext(state => state.studentsPanelOpen);

  const { data } = useStudentsRoster(activeSessionId, studentsPanelOpen);
  const [locallyRemovedIds, setLocallyRemovedIds] = useState<number[]>([]);

  const removeMutation = useMutation({
    mutationFn: (studentId: number) => Axios.delete(
      Routes.plato_api_remove_student_live_session_path(activeSessionId!, studentId),
    ),
  });

  const invitedSet = useMemo(() => {
    const ids = invitedStudentIds?.length ? invitedStudentIds : (data?.invited_ids ?? []);
    return new Set(ids);
  }, [invitedStudentIds, data?.invited_ids]);

  const { joined, notYetJoined } = useMemo(() => {
    const invited = (data?.students ?? []).filter(s => invitedSet.has(s.id));
    const connectedSet = new Set(connectedStudentIds ?? []);
    const locallyRemoved = new Set(locallyRemovedIds);
    return {
      joined: invited.filter(s => connectedSet.has(s.id) && !locallyRemoved.has(s.id)),
      notYetJoined: invited.filter(s => !connectedSet.has(s.id) && !locallyRemoved.has(s.id)),
    };
  }, [data?.students, invitedSet, connectedStudentIds, locallyRemovedIds]);

  const total = joined.length + notYetJoined.length;
  const allJoined = total > 0 && notYetJoined.length === 0;

  const handleRemove = (student: RosterStudent) => {
    setLocallyRemovedIds(ids => [...ids, student.id]);

    removeMutation.mutate(student.id, {
      onError: () => {
        setLocallyRemovedIds(ids => ids.filter(id => id !== student.id));
        tpmToast(I18n.t('remove_student_error'), {
          msgHeading: I18n.t('remove_student_error_title'),
          msgType: 'error',
        });
      },
      onSuccess: () => {
        tpmToast(I18n.t('remove_student_toast', { name: student.full_name }), {
          msgHeading: I18n.t('remove_student_toast_title'),
        });
      },
    });
  };

  return (
    <div
      aria-label={I18n.t('students_panel_header', { count: total })}
      className={cn(
        'tw-flex tw-flex-col tw-w-[320px] tw-font-tci',
        // AIDEV-NOTE: PLAT-16888 — Fill the vertical space from the top of the toolbar button to
        // the top of the viewport. Radix Popover exposes the collision-checked available height
        // as a CSS var on its Content; reading it here keeps the panel aligned with the trigger.
        'tw-h-[var(--radix-popover-content-available-height)]',
      )}
      role="region"
    >
      <header
        className={cn(
          'tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-1.5',
          'tw-border-b tw-border-gray-200',
        )}
      >
        <Text className="tw-text-tci-black tw-m-0" size="lg" tag="h2" weight="bold">
          {I18n.t('students_panel_header', { count: total })}
        </Text>
        <Popover.Close asChild>
          <UnstyledButton
            aria-label={I18n.t('students_panel_close')}
            className={cn(
              'tw-h-7 tw-w-7 tw-flex tw-items-center tw-justify-center tw-rounded',
              'tw-text-gray-700 hover:tw-bg-gray-100',
              'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-tci-purple',
            )}
            type="button"
          >
            <Icon aria-hidden="true" icon="Close" size={16} />
          </UnstyledButton>
        </Popover.Close>
      </header>

      <div className="tw-flex-1 tw-overflow-y-auto tw-px-3">
        <section aria-labelledby="students-panel-joined-section-header" className="tw-py-2">
          <Text
            className={cn('tw-m-0 tw-text-tci-black')}
            id="students-panel-joined-section-header"
            spacing="sm"
            tag="h3"
            weight="semibold"
          >
            {I18n.t('students_panel_joined_section_header', { count: joined.length })}
          </Text>
          <ul className="tw-list-none tw-p-0 tw-mt-1">
            {joined.map(student => (
              <StudentRow
                key={student.id}
                showRemove
                student={student}
                onRemove={handleRemove}
              />
            ))}
          </ul>
        </section>
        <div className="tw-w-full tw-bg-light-grey tw-h-[2px] tw-rounded" />
        <section
          aria-labelledby="students-panel-not-yet-joined-section-header"
          className="tw-py-2"
        >
          <Text
            className={cn('tw-m-0 tw-text-tci-black')}
            id="students-panel-not-yet-joined-section-header"
            spacing="sm"
            tag="h3"
            weight="semibold"
          >
            {I18n.t('students_panel_not_yet_joined_section_header', { count: notYetJoined.length })}
          </Text>
          {allJoined ? (
            <p className="tw-py-3 tw-text-sm tw-text-gray-600 tw-m-0">
              {I18n.t('students_panel_empty_not_yet_joined')}
            </p>
          ) : (
            <ul className="tw-list-none tw-p-0 tw-mt-1">
              {notYetJoined.map(student => (
                <StudentRow inactive key={student.id} student={student} />
              ))}
            </ul>
          )}
        </section>
      </div>

      <footer className="tw-px-3 tw-py-2 tw-border-t tw-border-gray-200">
        <UnstyledButton
          className={cn(
            'tw-w-full tw-py-2 tw-px-3 tw-rounded tw-font-tci',
            'tw-bg-tci-purple tw-text-white tw-font-semibold',
            'hover:tw-bg-tci-purple/90',
            'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2',
            'focus-visible:tw-outline-tci-purple',
          )}
          type="button"
          onClick={openNotifyModal}
        >
          {I18n.t('students_panel_manage_students')}
        </UnstyledButton>
      </footer>
    </div>
  );
};

export default StudentsPanel;
